import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "../api/client";
import { useJob } from "../api/hooks";
import { enqueuedResponseSchema, previewResultSchema, type PreviewResult, type RuleSet } from "../../api/contracts";
import { formatDay, formatMinutes, formatPct } from "../lib/format";
import { summarizePreview } from "../lib/locations";
import { ACTION_LABEL, flattenDay, groupPreview, PLANNED_TONE, type PreviewRow } from "../lib/preview";
import { explainReason, voiceOfPlanned } from "../lib/activity-reason";
import { Badge, EmptyState, ErrorNote, Panel, Spinner, Stat } from "./ui";
import { DataTable, type Column } from "./DataTable";

function columns(admin: boolean): Column<PreviewRow>[] {
  return [
    { key: "staff", header: "Staff", width: "11rem", cell: (r) => (r.first ? <span className="font-medium">{r.staffName}</span> : null) },
    { key: "action", header: "Action", width: "7rem", cell: (r) => <Badge tone={PLANNED_TONE[r.action.type]}>{ACTION_LABEL[r.action.type]}</Badge> },
    { key: "why", header: "Why", cell: (r) => {
      const a = r.action;
      const why = explainReason({ reason: a.reason, edge: a.edge, gapMinutes: a.gapMinutes, utilizationPct: a.utilizationPct, voice: voiceOfPlanned(a.type) });
      return (
        <>
          <span className="block min-w-[16rem] text-hs-text-muted">{why.sentence}</span>
          {admin && why.recognised && <span className="mt-1 block min-w-[16rem] text-xs text-hs-text-muted">{a.reason}</span>}
        </>
      );
    } },
    { key: "gap", header: "Open at start/end", align: "right", width: "9rem", cell: (r) => <span>{formatMinutes(r.action.gapMinutes)}</span> },
    { key: "booked", header: "Booked", align: "right", width: "5rem", cell: (r) => <span>{formatPct(r.action.utilizationPct)}</span> },
  ];
}

export interface Preview {
  /** Queues the preview job. */
  start: () => void;
  /** Forgets the current run (the location or the rules changed). */
  reset: () => void;
  running: boolean;
  result: PreviewResult | null;
  error: unknown;
}

/**
 * Queues a preview of `blvdId`'s window and polls the job. With `rules` (the Rules page's unsaved
 * draft) the preview plans with those instead of the location's saved rules.
 */
export function usePreview(blvdId: string, rules?: RuleSet): Preview {
  const [jobId, setJobId] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => api(`/api/locations/${blvdId}/preview`, enqueuedResponseSchema, { method: "POST", body: rules ? { rules } : undefined }),
    onSuccess: (r) => setJobId(r.jobId),
  });
  const job = useJob(jobId);
  const parsed = job.data?.status === "done" ? previewResultSchema.safeParse(job.data.result) : null;
  const result = parsed?.success ? parsed.data : null;
  const running = run.isPending || (jobId !== null && !!job.data && (job.data.status === "queued" || job.data.status === "running"));
  const error = run.error ?? job.error
    ?? (job.data?.status === "failed" ? new Error(job.data.lastError ?? "The preview failed.") : null)
    ?? (parsed && !parsed.success ? new Error("The preview finished but returned an unexpected result. Try again; if it repeats, contact corporate.") : null);
  return { start: () => run.mutate(), reset: () => { run.reset(); setJobId(null); }, running, result, error };
}

/** The spinner while a preview runs and any error it ended in. */
export function PreviewStatus({ preview }: { preview: Preview }) {
  return (
    <>
      <ErrorNote error={preview.error} />
      {preview.running && <Spinner label="Reviewing shifts, appointments and existing blocks…" />}
    </>
  );
}

/** A finished preview: the four counts, the window line and one table per day. */
export function PreviewResults({ result, locationName, admin }: { result: PreviewResult | null; locationName: string | undefined; admin: boolean }) {
  if (!result) return null;
  const s = summarizePreview(result.actions);
  const days = groupPreview(result.actions);
  const cols = columns(admin);
  return (
    <>
      <div className="mb-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Add" value={s.create} />
        <Stat label="Remove" value={s.remove} />
        <Stat label="Resize" value={s.resize} />
        <Stat label="No change" value={s.skip} />
      </div>
      <p className="mb-4 text-[13px] text-hs-text-muted"><span className="font-medium text-hs-text">{locationName}</span> · {formatDay(result.from)} to {formatDay(result.to)} · {result.shifts} shifts</p>
      {days.length === 0 && <EmptyState title="Nothing to do" body={result.draftRules ? "Every shift in the window already looks right under these rules." : "Every shift in the window already looks right under the current rules."} />}
      <div className="space-y-4">
        {days.map((d) => (
          <Panel key={d.date} title={formatDay(d.date)} padding="none">
            <DataTable columns={cols} rows={flattenDay(d)} rowKey={(r) => r.key} density="compact" caption={`Planned actions for ${formatDay(d.date)}`} />
          </Panel>
        ))}
      </div>
    </>
  );
}
